import type {BranchesPagination, CloseReason, VendorBranch} from './types';

import {
  CreditCardOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LogoutOutlined,
  NotificationOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  ShopOutlined,
  ToolOutlined
} from '@ant-design/icons';
import {Divider} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useFlag} from '@unleash/proxy-client-react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  setSelectedVendor,
  updateBranches,
  updateGetBranchesPage
} from 'store/slices/dashboard/slicer';
import {getBranches} from 'store/slices/dashboard/thunk';
import {
  getReasons,
  getVendorGrade,
  getVendorSheetUrl,
  setClosed,
  setOpenVendor
} from 'store/slices/profile/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import ChangeVendorButton from './components/ChangeVendorButton/ChangeVendorButton';
import LogoutBottomSheet from './components/LogoutBottomSheet/LogoutBottomSheet';
import ProfileItem from './components/ProfileItem/ProfileItem';
import StoreStatusBottomSheet from './components/StoreStatusBottomSheet/StoreStatusBottomSheet';
import VendorGradeBadge from './components/VendorGradeBadge/VendorGradeBadge';
import VendorSelectorBottomSheet from './components/VendorSelectorBottomSheet/VendorSelectorBottomSheet';
import WarehouseReportModal from './components/WarehouseReportModal/WarehouseReportModal';

type BranchRequestParams = {
  page: number;
  pageSize: number;
  searchQuery?: string;
  vendorStatus?: 'active' | 'deactive';
};

type ProfileMenuItem = {
  key: string;
  title: string;
  icon: React.ReactNode;
  hasBadge?: boolean;
  isLogoutItem?: boolean;
  hidden?: boolean;
  onClick: () => void;
};

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {vendorId: routeVendorId} = useParams<{vendorId: string}>();
  const promotionCenterEnabled = useFlag('promotionCenter');
  const {
    branches,
    loadingBranches,
    getBranchesPage,
    branchesPagination,
    selectedVendor
  } = useAppSelector((state) => state.dashboard) as {
    branches: VendorBranch[];
    loadingBranches: boolean;
    getBranchesPage: number;
    branchesPagination: BranchesPagination;
    selectedVendor?: VendorBranch;
  };
  const {vendorGrade, reasons, settingUpCloseVendor, settingUpOpenVendor} = useAppSelector(
    (state) => state.profile
  ) as {
    vendorGrade?: {grade?: string};
    reasons: CloseReason[];
    settingUpCloseVendor: boolean;
    settingUpOpenVendor: boolean;
  };

  const [showVendorSelector, setShowVendorSelector] = useState(false);
  const [showStatusSheet, setShowStatusSheet] = useState(false);
  const [showLogoutSheet, setShowLogoutSheet] = useState(false);
  const [showWarehouseReportModal, setShowWarehouseReportModal] = useState(false);
  const [requestLinkLoading, setRequestLinkLoading] = useState(false);
  const hasInitializedBranches = useRef(false);
  const [branchRequestParams, setBranchRequestParams] = useState<BranchRequestParams>({
    page: 0,
    pageSize: 10
  });

  const storedVendor = getSelectedVendorFromStorage() as VendorBranch | null;
  const currentVendor = selectedVendor ?? storedVendor;
  const currentVendorId = currentVendor?.id;

  useEffect(() => {
    if (currentVendorId) {
      void dispatch(getVendorGrade({vendor_id: currentVendorId}));
      if (String(routeVendorId) !== String(currentVendorId)) {
        navigate(`/profile/${currentVendorId}`, {replace: true});
      }
    }
  }, [currentVendorId, dispatch, navigate, routeVendorId]);

  useEffect(() => {
    if (hasInitializedBranches.current) {
      return;
    }
    hasInitializedBranches.current = true;

    if (!selectedVendor && storedVendor) {
      dispatch(setSelectedVendor(storedVendor as never));
    }
    if (branches.length === 0) {
      void dispatch(getBranches({page: 0, pageSize: 10}));
    }
  }, [branches.length, dispatch, selectedVendor, storedVendor]);

  const openVendorSelector = () => {
    setBranchRequestParams({page: 0, pageSize: 10});
    dispatch(updateGetBranchesPage(0));
    setShowVendorSelector(true);
  };

  const closeVendorSelector = () => {
    setShowVendorSelector(false);
    setBranchRequestParams({page: 0, pageSize: 10});
    dispatch(updateGetBranchesPage(0));
    void dispatch(getBranches({page: 0, pageSize: 10}));
  };

  const handleFilterBranches = (vendorStatus?: 'active' | 'deactive') => {
    const params = {...branchRequestParams, page: 0, vendorStatus};
    setBranchRequestParams(params);
    dispatch(updateGetBranchesPage(0));
    void dispatch(getBranches(params));
  };

  const handleSearchBranches = (searchQuery: string) => {
    const params = {
      ...branchRequestParams,
      page: 0,
      searchQuery: searchQuery || undefined
    };
    setBranchRequestParams(params);
    dispatch(updateGetBranchesPage(0));
    void dispatch(getBranches(params));
  };

  const handleLoadMoreBranches = () => {
    const page = getBranchesPage + 1;
    dispatch(updateGetBranchesPage(page));
    void dispatch(getBranches({...branchRequestParams, page}));
  };

  const handleSelectVendor = (vendorId: string) => {
    const branch = branches.find((item) => String(item.id) === vendorId);
    if (!branch) {
      return;
    }
    dispatch(setSelectedVendor(branch as never));
    void dispatch(getVendorGrade({vendor_id: branch.id}));
    closeVendorSelector();
  };

  const openStatusSheet = () => {
    if (currentVendor?.attributes.isActive && reasons.length === 0) {
      void dispatch(getReasons());
    }
    setShowStatusSheet(true);
  };

  const handleDeactivateVendor = async (reasonId: string, closedUntil: string) => {
    if (!currentVendor) {
      return;
    }
    await dispatch(setClosed({vendorId: currentVendor.id, reasonId, closedUntil}));
    dispatch(updateBranches(currentVendor as never));
    setShowStatusSheet(false);
  };

  const handleActivateVendor = async () => {
    if (!currentVendor) {
      return;
    }
    await dispatch(setOpenVendor(currentVendor.id));
    dispatch(updateBranches(currentVendor as never));
    setShowStatusSheet(false);
  };

  const handleVendorSheetRequest = useCallback(async () => {
    if (!currentVendorId) {
      return;
    }
    setRequestLinkLoading(true);
    try {
      const response = await dispatch(getVendorSheetUrl({vendor_id: currentVendorId})).unwrap();
      const reportUrl = response.data?.warehouse_report_sheet;
      if (!reportUrl) {
        toast('برای فروشگاه شما لینک جزئیات مغایرت موجودی در دسترس نیست.');
        setShowWarehouseReportModal(false);
        return;
      }
      window.open(reportUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast('خطا در دریافت لینک');
      setShowWarehouseReportModal(false);
    } finally {
      setRequestLinkLoading(false);
    }
  }, [currentVendorId, dispatch]);

  const menuItems = useMemo<ProfileMenuItem[]>(
    () => [
      {
        key: 'promotion',
        title: 'ترقی',
        icon: <RocketOutlined aria-hidden />,
        hasBadge: true,
        hidden: !promotionCenterEnabled,
        onClick: () => navigate('/services/promotion-center/')
      },
      {
        key: 'tickets',
        title: 'پشتیبانی آنلاین',
        icon: <ToolOutlined aria-hidden />,
        onClick: () =>
          navigate({
            pathname: '/tickets',
            search: new URLSearchParams({from: location.pathname}).toString()
          })
      },
      {
        key: 'tracking',
        title: 'پیگیری‌ها',
        icon: <FileTextOutlined aria-hidden />,
        onClick: () => navigate('/tracking')
      },
      {
        key: 'invoice',
        title: 'صورتحساب',
        icon: <CreditCardOutlined aria-hidden />,
        onClick: () => navigate('/invoice')
      },
      {
        key: 'service-area',
        title: 'محدوده ارسال',
        icon: <EnvironmentOutlined aria-hidden />,
        onClick: () => navigate(`/services/service-area/${currentVendorId}`)
      },
      {
        key: 'faq',
        title: 'سوالات متداول',
        icon: <QuestionCircleOutlined aria-hidden />,
        onClick: () => navigate('/faq')
      },
      {
        key: 'warehouse-report',
        title: 'اطلاعیه‌ها و هشدارها',
        icon: <NotificationOutlined aria-hidden />,
        onClick: () => setShowWarehouseReportModal(true)
      },
      {
        key: 'logout',
        title: 'خروج',
        icon: <LogoutOutlined aria-hidden />,
        isLogoutItem: true,
        onClick: () => setShowLogoutSheet(true)
      }
    ],
    [currentVendorId, location.pathname, navigate, promotionCenterEnabled]
  );

  return (
    <>
      <PageLayout contentClassName="flex flex-col bg-white p-4">
        <ChangeVendorButton
          title={currentVendor?.attributes.title ?? ''}
          onClick={openVendorSelector}
        />
        {vendorGrade?.grade && (
          <div className="pt-2">
            <VendorGradeBadge grade={vendorGrade.grade} />
          </div>
        )}
        <Divider className="my-2 bg-gray-02" size={2} />
        <ProfileItem
          title="وضعیت فروشگاه"
          icon={<ShopOutlined aria-hidden />}
          status={currentVendor?.attributes.isActive ? 'active' : 'inactive'}
          onClick={openStatusSheet}
        />
        {menuItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <ProfileItem
              key={item.key}
              title={item.title}
              icon={item.icon}
              hasBadge={item.hasBadge}
              isLogoutItem={item.isLogoutItem}
              loading={item.key === 'warehouse-report' && requestLinkLoading}
              onClick={item.onClick}
            />
          ))}
      </PageLayout>

      <VendorSelectorBottomSheet
        show={showVendorSelector}
        branches={branches}
        pagination={branchesPagination}
        page={getBranchesPage}
        loading={loadingBranches}
        selectedVendorId={currentVendorId}
        onClose={closeVendorSelector}
        onSelect={handleSelectVendor}
        onLoadMore={handleLoadMoreBranches}
        onSearch={handleSearchBranches}
        onFilter={handleFilterBranches}
      />
      <StoreStatusBottomSheet
        show={showStatusSheet}
        isActive={Boolean(currentVendor?.attributes.isActive)}
        reasons={reasons}
        loading={settingUpCloseVendor || settingUpOpenVendor}
        onClose={() => setShowStatusSheet(false)}
        onActivate={handleActivateVendor}
        onDeactivate={handleDeactivateVendor}
      />
      <WarehouseReportModal
        show={showWarehouseReportModal}
        loading={requestLinkLoading}
        onClose={() => setShowWarehouseReportModal(false)}
        onConfirm={handleVendorSheetRequest}
      />
      <LogoutBottomSheet
        show={showLogoutSheet}
        onClose={() => setShowLogoutSheet(false)}
      />
    </>
  );
};

export default Profile;
